import React, { PropsWithChildren, ReactNode } from "react"
import { AppBar, Grid, IconButton, MenuItem, Select, Stack, Toolbar, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes } from "@fortawesome/pro-light-svg-icons/faTimes"
import { faChevronLeft } from "@fortawesome/pro-light-svg-icons/faChevronLeft"
import { faEllipsisV } from "@fortawesome/pro-light-svg-icons/faEllipsisV"
import { WellknownHeight } from "../../../constants/wellknown"
import { LocalizedEnum } from "../../../modules/localization/components/AppLocalized"
import { SafeArea } from "./SafeArea"
import { useInView } from "react-intersection-observer"
import { useLocaleSelect } from "../../../modules/localization/localizationHooks"
import { tailwindPalette } from "../../../modules/theme/tailwindPalette"
import { KeyboardArrowLeftTwoTone } from "@mui/icons-material"
import { faCircleXmark } from "@fortawesome/pro-light-svg-icons"

const useStandardTopbarStyles = makeStyles()((theme) => ({
    root: {
        backgroundColor: theme.palette.common.white,
    },
    bottomGrid: {
        width: "100%",
    },
    spacer: {
        paddingBottom: WellknownHeight.PRIMARY_TOP_BAR,
    },
    hidden: {
        visibility: "hidden",
    },
    languageSelect: {
        fontSize: "0.9rem",
        // Gets rid of standard underline
        "&::before": {
            borderBottom: "none",
        },
        // Gets rid of blue underline when focused
        "&::after": {
            borderBottom: "none",
        },
    },
    languageSelectRoot: {
        // Gets rid of grey background color when focused
        "&.MuiSelect-select:focus": {
            backgroundColor: "transparent",
        },
    },
}))

interface StandardTopbarProps {
    middleElement?: React.ReactNode
    bottomElement?: React.ReactNode
    backButtonType?: "close" | "back" | "none"
    hideMenuButton?: boolean
    hideSpacer?: boolean
    onBackButton: (() => void) | undefined
    onMenuButton: (() => void) | undefined
}

export function StandardTopbar(props: StandardTopbarProps) {
    const {
        middleElement,
        bottomElement,
        backButtonType = "close",
        hideMenuButton = false,
        hideSpacer = false,
        onBackButton,
        onMenuButton,
    } = props

    const { classes, cx } = useStandardTopbarStyles()
    const { ref: spacerRef, inView: isAtTop } = useInView({ threshold: 0.5 })
    const elevation = isAtTop ? 0 : 4

    return (
        <>
            <AppBar position="sticky" elevation={elevation} className={classes.root}>
                <Toolbar>
                    <SafeArea position="relative">
                        <Grid container direction="column" wrap="nowrap">
                            <Grid item container wrap="nowrap" justifyContent="space-between" alignItems="center">
                                <Grid item>
                                    <IconButton
                                        disabled={!onBackButton}
                                        onClick={onBackButton}
                                        edge="start"
                                        color="inherit"
                                        aria-label="close"
                                        className={cx({
                                            [classes.hidden]: backButtonType === "none",
                                        })}
                                    >
                                        <FontAwesomeIcon
                                            icon={backButtonType === "close" ? faTimes : faChevronLeft}
                                            color="black"
                                            size="1x"
                                        />
                                    </IconButton>
                                </Grid>

                                <Grid item>{middleElement}</Grid>

                                <Grid item>
                                    <IconButton
                                        disabled={!onMenuButton}
                                        onClick={onMenuButton}
                                        color="inherit"
                                        aria-label="menu"
                                        className={cx({
                                            [classes.hidden]: hideMenuButton,
                                        })}
                                    >
                                        <FontAwesomeIcon icon={faEllipsisV} color="black" size="1x" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            {bottomElement && (
                                <Grid item className={classes.bottomGrid}>
                                    {bottomElement}
                                </Grid>
                            )}
                        </Grid>
                    </SafeArea>
                </Toolbar>
            </AppBar>
            {!hideSpacer && <Toolbar ref={spacerRef} className={classes.spacer} />}
        </>
    )
}

const useTitleStyles = makeStyles()((_theme) => ({
    title: {
        color: "#505050",
        fontSize: "1.0625rem", // 17px @ std
        fontWeight: "bold",
        letterSpacing: "0.1px",
        whiteSpace: "nowrap",
    },
}))

type StandardTopbarTitleProps = Readonly<PropsWithChildren<{}>>

export function StandardTopbarTitle({ children }: StandardTopbarTitleProps) {
    const { classes } = useTitleStyles()

    return <Typography className={classes.title}>{children}</Typography>
}

type LanguageTopbarProps = Readonly<{}>

export function LanguageTopbar({}: LanguageTopbarProps) {
    const { classes } = useStandardTopbarStyles()
    const { supportedLocales, selectedLocale, selectLocale } = useLocaleSelect()

    return (
        <AppBar position="fixed" elevation={0} className={classes.root}>
            <Toolbar>
                <SafeArea top position="relative">
                    <Select
                        variant="standard"
                        value={selectedLocale}
                        onChange={(event) => selectLocale(event.target.value as string)}
                        className={classes.languageSelect}
                        classes={{ select: classes.languageSelectRoot }}
                    >
                        {supportedLocales.map((locale) => (
                            <MenuItem key={locale} value={locale}>
                                <LocalizedEnum base="language-topbar-locale" enumValue={locale}>
                                    <span>{locale}</span>
                                </LocalizedEnum>
                            </MenuItem>
                        ))}
                    </Select>
                </SafeArea>
            </Toolbar>
        </AppBar>
    )
}

interface StandardDialogTopbarProps {
    dialogTitle?: ReactNode
    titleLeftIcon?: ReactNode
    subtitle?: ReactNode
    hideCloseIcon: boolean
    handleBackButton?: () => void
    backButtonText?: string
    extraActions?: ReactNode
    onClose: () => void
    dialogTitleClasses?: string
}

const useStandardDialogTopbarStyles = makeStyles()((theme) => ({
    dialogTopbar: {
        padding: "0.5rem 0.5rem 2rem",
        gap: "0.75rem",
    },
    dialogTitle: {
        paddingTop: "2.25rem",
        fontWeight: 600,
    },
    dialogTitleColor: {
        color: tailwindPalette.slate[700],
    },
    subtitle: {
        color: theme.palette.text.secondary,
        paddingTop: "0.25rem",
        padding: "0.25rem 2rem 0 2rem",
    },
    backButton: {
        textDecoration: "underline",

        "&:hover": {
            backgroundColor: "transparent",
            textDecoration: "none",
        },
    },
}))

export function StandardDialogTopbar(props: StandardDialogTopbarProps) {
    const {
        dialogTitle,
        titleLeftIcon,
        dialogTitleClasses,
        subtitle,
        hideCloseIcon,
        handleBackButton,
        backButtonText,
        extraActions,
        onClose,
    } = props

    const { classes, cx } = useStandardDialogTopbarStyles()

    return (
        <Grid container direction="column" wrap="nowrap" className={classes.dialogTopbar}>
            {(!hideCloseIcon || !!handleBackButton || extraActions) && (
                <Stack flexDirection="row" alignItems="center" gap={3}>
                    {handleBackButton && (
                        <IconButton
                            onClick={handleBackButton}
                            className={cx(classes.dialogTitleColor, backButtonText && classes.backButton)}
                        >
                            <KeyboardArrowLeftTwoTone />
                            {backButtonText && <Typography>{backButtonText}</Typography>}
                        </IconButton>
                    )}
                    {!hideCloseIcon && (
                        <Grid container justifyContent="flex-end" alignItems="center" gap={3}>
                            {extraActions ?? null}
                            <IconButton onClick={onClose}>
                                <FontAwesomeIcon icon={faCircleXmark} size="sm" />
                            </IconButton>
                        </Grid>
                    )}
                </Stack>
            )}

            <Grid
                item
                container
                direction="row"
                wrap="nowrap"
                justifyContent="center"
                alignItems="center"
                gap="1.5rem"
                className={cx({ [classes.dialogTitle]: hideCloseIcon && !handleBackButton }, dialogTitleClasses)}
            >
                {titleLeftIcon}
                <Typography variant="h4" align="center" className={classes.dialogTitleColor}>
                    {dialogTitle}
                </Typography>
            </Grid>
            {subtitle && (
                <Typography variant="h6" align="center" className={classes.subtitle}>
                    {subtitle}
                </Typography>
            )}
        </Grid>
    )
}
